import { BehaviorSubject } from 'rxjs';
import { FsSelectorPeriod } from '../../utils/common/FsSelectorPeriod';
import { Fund } from '../../../fund-performance/dto/Fund';
import { ManagementEntity } from '../../../organization/dto/ManagementEntity';
import { Injectable } from '@angular/core';
import { isEqual } from 'lodash';
import { FinancialStatement } from '../../models/common/FinancialStatement';

const savedFsTabLocalStorageKey = 'savedFsTabIndex';
const savedFsOwnershipEntity = 'savedFsOwnershipEntity';
@Injectable({
  providedIn: 'root',
})
export class FsGettersSettersService {
  private _financialStatement = new BehaviorSubject<FinancialStatement>(null);

  private _selectedEntityPeriod: BehaviorSubject<FsSelectorPeriod> = new BehaviorSubject<FsSelectorPeriod>(null);
  private _ownershipEntityPeriods: BehaviorSubject<FsSelectorPeriod[]> = new BehaviorSubject<FsSelectorPeriod[]>(undefined);
  private _ownershipEntities: BehaviorSubject<(Fund|ManagementEntity)[]> = new BehaviorSubject<(Fund|ManagementEntity)[]>([]);
  private _selectedOwnershipEntity: BehaviorSubject<Fund|ManagementEntity> = new BehaviorSubject<(Fund|ManagementEntity)>(null);
  private _showCopyPeriodModal: BehaviorSubject<boolean> = new BehaviorSubject<(boolean)>(false);
  private _showCreatePeriodModal: BehaviorSubject<boolean> = new BehaviorSubject<(boolean)>(false);
  private _showDeleteModal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isCapAccountManagementLocked: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isShownManageInvestorsModal: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isShownBulkUploadManualValues: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  static getSavedTab(): string {
    return JSON.parse(sessionStorage.getItem(savedFsTabLocalStorageKey)) as string;
  }

  static setSavedTab(savedTab: string): void {
    sessionStorage.setItem(savedFsTabLocalStorageKey, JSON.stringify(savedTab));
  }

  static getSavedFsOwnershipEntityId(): number {
    return Number(sessionStorage.getItem(savedFsOwnershipEntity));
  }

  static setSavedFsOwnershipEntityId(savedFsOwnershipEntityId: number): void {
    sessionStorage.setItem(savedFsOwnershipEntity, JSON.stringify(savedFsOwnershipEntityId));
  }

  // Getters
  get financialStatement(): BehaviorSubject<FinancialStatement> {
    return this._financialStatement;
  }

  get ownershipEntityPeriods(): BehaviorSubject<FsSelectorPeriod[]> {
    return this._ownershipEntityPeriods;
  }

  get selectedEntityPeriod(): BehaviorSubject<FsSelectorPeriod> {
    return this._selectedEntityPeriod;
  }

  get ownershipEntities(): BehaviorSubject<(Fund | ManagementEntity)[]> {
    return this._ownershipEntities;
  }

  get selectedOwnershipEntity(): BehaviorSubject<Fund | ManagementEntity> {
    return this._selectedOwnershipEntity;
  }

  get showCopyPeriodModal(): BehaviorSubject<boolean> {
    return this._showCopyPeriodModal;
  }

  get showCreatePeriodModal(): BehaviorSubject<boolean> {
    return this._showCreatePeriodModal;
  }

  get showDeleteModal(): BehaviorSubject<boolean> {
    return this._showDeleteModal;
  }

  get isCapAccountManagementLocked(): BehaviorSubject<boolean> {
    return this._isCapAccountManagementLocked;
  }

  get isShownManageInvestorsModal(): BehaviorSubject<boolean> {
    return this._isShownManageInvestorsModal;
  }

  get isShownBulkUploadManualValues(): BehaviorSubject<boolean> {
    return this._isShownBulkUploadManualValues;
  }

  // Setters
  setFinancialStatement(newFs: FinancialStatement): BehaviorSubject<FinancialStatement> {
    if (!this.financialStatement.value && !newFs) {
      return;
    }
    this._financialStatement.next(newFs);
  }

  setOwnershipEntityPeriods(newPeriods: FsSelectorPeriod[]): void {
    if (!newPeriods) {
      return;
    }
    const existingData = this._ownershipEntityPeriods.value?.map((p) => ({
      period: p.period,
      status: p.status,
    }));
    const newData = newPeriods.map((p) => ({
      period: p.period,
      status: p.status,
    }));
    if (isEqual(existingData, newData)) {
      return;
    }
    this._ownershipEntityPeriods.next(newPeriods);
  }

  setSelectedEntityPeriod(newSelectedPeriod: FsSelectorPeriod): void {
    if (!newSelectedPeriod || newSelectedPeriod.period === this._selectedEntityPeriod.value?.period) {
      return;
    }
    this._selectedEntityPeriod.next(newSelectedPeriod);
  }

  setOwnershipEntities(newOwnershipEntities: (Fund | ManagementEntity)[]): void {
    const newOwnershipUuids: string [] = newOwnershipEntities.map((o) => o.uuid);
    const existingOwnershipUuids: string [] = this._ownershipEntities.value.map((o) => o.uuid);
    if (isEqual(newOwnershipUuids, existingOwnershipUuids)) {
      return;
    }
    this._ownershipEntities.next(newOwnershipEntities);
  }

  setSelectedOwnershipEntity(newSelectedEntity: Fund | ManagementEntity): void {
    if (!newSelectedEntity || newSelectedEntity.uuid === this._selectedOwnershipEntity.value?.uuid) {
      return;
    }
    this._selectedOwnershipEntity.next(newSelectedEntity);
  }

  setShowCopyPeriodModal(value: boolean): void {
    this._showCopyPeriodModal.next(value);
  }

  setShowCreatePeriodModal(value: boolean): void {
    this._showCreatePeriodModal.next(value);
  }

  setShowDeleteModal(value: boolean): void {
    this._showDeleteModal.next(value);
  }

  setIsCapAccountManagementLocked(value: boolean): void {
    this._isCapAccountManagementLocked.next(value);
  }

  setIsShownManageInvestorsModal(value: boolean): void {
    this._isShownManageInvestorsModal.next(value);
  }

  setIsShownBulkUploadManualValues(value: boolean): void {
    this._isShownBulkUploadManualValues.next(value);
  }
}
